import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import wsPrintClient from '../../services/wsPrintClient';
import printerService from '../../services/printerService';

// Criar o contexto do Onboard
const OnboardContext = createContext();

const getInitialPrinterType = () => {
  try {
    const savedType = localStorage.getItem('printerType');
    if (savedType === 'websocket') return 'socket';
    if (savedType === 'bluetooth' || savedType === 'socket' || savedType === 'webbluetooth') {
      return savedType;
    }
  } catch (error) {
    void error;
  }
  return 'bluetooth';
};

// Provider do contexto Onboard
export const OnboardProvider = ({ children }) => {
  const { user, vehicleUsageToday } = useAuth();
  const [onboardData, setOnboardData] = useState({
    dtUso: '', // Data de uso atual
    userInfo: null,
    vehicleData: null,
    empresasData: [],
    totaisPorCliente: [],
    coletas: [],
    notasDoCliente: [], // Novo estado para armazenar as notas do cliente selecionado
    totals: {
      notas: 0,
      vols: 0,
      peso: 0,
      m3: 0,
      falta: 0,
      notasImpressas: 0,
      notasNaoImpressas: 0,
      etiquetasImpressas: 0,
      etiquetasNaoImpressas: 0,
      primeiraEtiqueta: null,
      ultimaEtiqueta: null
    }
  });
  // Nota selecionada externamente (contexto global)
  const [notaAtual, setNotaAtual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const [printerName, setPrinterName] = useState(null);
  const [printerType, setPrinterType] = useState(getInitialPrinterType);
  const [printerDevice, setPrinterDevice] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerEmulator, setPrinterEmulator] = useState(null);
  const [cnpjAtual, setCnpjAtual] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [veiculoDoDia, setVeiculoDoDia] = useState(null);
  const [modalPlacaVisible, setModalPlacaVisible] = useState(false);

  useEffect(() => {
    if (user) {
      printerService.setUser(user);
    }
  }, [user]);


  const buscarNotasTotais = useCallback(async (veiculoId, dtUso, usuarioId) => {
    try {
      const placaFallback = (veiculoDoDia?.veiculo?.placa || veiculoDoDia?.placa || onboardData?.vehicleData?.placa || '').toUpperCase();
      if (!veiculoId || !dtUso || !usuarioId) return;
      // Buscar apenas totais agregados por cliente
      try {
        const totais = await apiService.notaVeiculoUso.findTotalsPorVeiculo({ veiculoId, dia: dtUso, usuarioId });

        const arr = Array.isArray(totais) ? totais : [];
        const sumNotas = arr.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0) + Number(it?.notasImpressas || 0), 0);
        const sumPeso = arr.reduce((s, it) => s + (typeof it?.pesoTotal === 'string' ? parseFloat(it.pesoTotal) : Number(it?.pesoTotal || 0)), 0);
        const sumM3 = arr.reduce((s, it) => s + (typeof it?.m3Total === 'string' ? parseFloat(it.m3Total) : Number(it?.m3Total || 0)), 0);
        const sumVols = arr.reduce((s, it) => s + (typeof it?.volumesTotal === 'string' ? parseFloat(it.volumesTotal) : Number(it?.volumesTotal || 0)), 0);
        const sumVolsNaoImp = arr.reduce((s, it) => s + (typeof it?.volumesNaoImpressosTotal === 'string' ? parseFloat(it.volumesNaoImpressosTotal) : Number(it?.volumesNaoImpressosTotal || 0)), 0);

        const sumNotasImp = arr.reduce((s, it) => s + Number(it?.notasImpressas || 0), 0);
        const sumNotasNaoImp = arr.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0), 0);
        const sumEtiquetasImp = arr.reduce((s, it) => s + (Number(it?.volumesTotal || 0) - Number(it?.volumesNaoImpressosTotal || 0)), 0);
        const sumEtiquetasNaoImp = arr.reduce((s, it) => s + Number(it?.volumesNaoImpressosTotal || 0), 0);

        // Buscar notas individuais para pegar as datas da primeira e última etiqueta
        let minData = null;
        let maxData = null;
        try {
          const notasIndividuais = await apiService.notaVeiculoUso.list({ veiculoId, dia: dtUso, usuarioId });

          if (Array.isArray(notasIndividuais)) {
            // Criar um mapa de notas por remetente para popular as coletas
            const notasPorRemetente = {};

            notasIndividuais.forEach(n => {
              const baseNota = n.nota || {};
              // Normalizar cidade e UF se estiverem faltando no destinatário
              if (baseNota.destinatario) {
                if (!baseNota.destinatario.cidade && baseNota.cidade) {
                  baseNota.destinatario.cidade = baseNota.cidade;
                }
                if (!baseNota.destinatario.uf && baseNota.uf) {
                  baseNota.destinatario.uf = baseNota.uf;
                }
              }

              const cdremetente = baseNota.cdremetente;
              if (cdremetente) {
                if (!notasPorRemetente[cdremetente]) {
                  notasPorRemetente[cdremetente] = [];
                }

                // Usar createdAt como primeira_etiqueta e ultima_etiqueta do backend
                const notaComDatas = {
                  ...n,
                  nota: baseNota,
                  primeira_etiqueta: n.createdAt,
                  ultima_etiqueta: n.ultima_etiqueta
                };

                notasPorRemetente[cdremetente].push(notaComDatas);
              }

              if (n.status === 1) { // Apenas impressas têm datas relevantes de etiqueta
                // Primeira etiqueta (createdAt)
                if (n.createdAt) {
                  const d = new Date(n.createdAt);
                  if (!minData || d < minData) minData = d;
                }
                // Última etiqueta (campo ultima_etiqueta vindo do backend)
                if (n.ultima_etiqueta) {
                  const d = new Date(n.ultima_etiqueta);
                  if (!maxData || d > maxData) maxData = d;
                }
              }
            });

            // Mesclar as notas em cada item de totaisPorCliente
            arr.forEach(item => {
              if (item.cdremetente && notasPorRemetente[item.cdremetente]) {
                item.notas = notasPorRemetente[item.cdremetente];
              } else {
                item.notas = [];
              }
            });
          }
        } catch (errDate) {
          console.error("Erro ao buscar datas de etiquetas:", errDate);
        }

        setOnboardData(prev => ({
          ...prev,
          totaisPorCliente: arr,
          coletas: arr, // Sincronizar coletas com totaisPorCliente para a aba produtividade
          totals: {
            ...(prev.totals || {}),
            notas: Number(sumNotas || 0),
            vols: Number(sumVols || 0),
            peso: Number(Number(sumPeso || 0).toFixed(2)),
            m3: Number(Number(sumM3 || 0).toFixed(2)),
            falta: Math.round(Number(sumVolsNaoImp || 0)),
            notasImpressas: sumNotasImp,
            notasNaoImpressas: sumNotasNaoImp,
            etiquetasImpressas: sumEtiquetasImp,
            etiquetasNaoImpressas: sumEtiquetasNaoImp,
            primeiraEtiqueta: minData,
            ultimaEtiqueta: maxData
          },
        }));
      } catch (error) {
        void error;
      }

      // Fallback: se nada veio com veiculoId/usuarioId, tentar por placa+dia
      try {
        const atual = onboardData?.totaisPorCliente || [];
        if ((!Array.isArray(atual) || atual.length === 0) && placaFallback) {
          const totaisViaPlaca = await apiService.notaVeiculoUso.findTotalsPorVeiculo({ placa: placaFallback, dia: dtUso });
          const arrPlaca = Array.isArray(totaisViaPlaca) ? totaisViaPlaca : [];
          const sumNotasP = arrPlaca.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0) + Number(it?.notasImpressas || 0), 0);
          const sumPesoP = arrPlaca.reduce((s, it) => s + (typeof it?.pesoTotal === 'string' ? parseFloat(it.pesoTotal) : Number(it?.pesoTotal || 0)), 0);
          const sumM3P = arrPlaca.reduce((s, it) => s + (typeof it?.m3Total === 'string' ? parseFloat(it.m3Total) : Number(it?.m3Total || 0)), 0);
          const sumVolsP = arrPlaca.reduce((s, it) => s + (typeof it?.volumesTotal === 'string' ? parseFloat(it.volumesTotal) : Number(it?.volumesTotal || 0)), 0);
          const sumVolsNaoImpP = arrPlaca.reduce((s, it) => s + (typeof it?.volumesNaoImpressosTotal === 'string' ? parseFloat(it.volumesNaoImpressosTotal) : Number(it?.volumesNaoImpressosTotal || 0)), 0);

          const sumNotasImpP = arrPlaca.reduce((s, it) => s + Number(it?.notasImpressas || 0), 0);
          const sumNotasNaoImpP = arrPlaca.reduce((s, it) => s + Number(it?.notasNaoImpressas || 0), 0);
          const sumEtiquetasImpP = arrPlaca.reduce((s, it) => s + (Number(it?.volumesTotal || 0) - Number(it?.volumesNaoImpressosTotal || 0)), 0);
          const sumEtiquetasNaoImpP = arrPlaca.reduce((s, it) => s + Number(it?.volumesNaoImpressosTotal || 0), 0);

          // Buscar notas individuais para pegar as datas da primeira e última etiqueta (fallback)
          let minDataP = null;
          let maxDataP = null;
          try {
            const notasIndividuaisP = await apiService.notaVeiculoUso.list({ placa: placaFallback, dia: dtUso });
            if (Array.isArray(notasIndividuaisP)) {
              // Criar um mapa de notas por remetente para popular as coletas (fallback)
              const notasPorRemetenteP = {};

              notasIndividuaisP.forEach(n => {
                const baseNota = n.nota || {};
                // Normalizar cidade e UF se estiverem faltando no destinatário
                if (baseNota.destinatario) {
                  if (!baseNota.destinatario.cidade && baseNota.cidade) {
                    baseNota.destinatario.cidade = baseNota.cidade;
                  }
                  if (!baseNota.destinatario.uf && baseNota.uf) {
                    baseNota.destinatario.uf = baseNota.uf;
                  }
                }

                const cdremetente = baseNota.cdremetente;
                if (cdremetente) {
                  if (!notasPorRemetenteP[cdremetente]) {
                    notasPorRemetenteP[cdremetente] = [];
                  }

                  // Usar createdAt como primeira_etiqueta e ultima_etiqueta do backend
                  const notaComDatas = {
                    ...n,
                    nota: baseNota,
                    primeira_etiqueta: n.createdAt,
                    ultima_etiqueta: n.ultima_etiqueta
                  };

                  notasPorRemetenteP[cdremetente].push(notaComDatas);
                }

                if (n.status === 1) {
                  // Primeira etiqueta (createdAt)
                  if (n.createdAt) {
                    const d = new Date(n.createdAt);
                    if (!minDataP || d < minDataP) minDataP = d;
                  }
                  // Última etiqueta (campo ultima_etiqueta vindo do backend)
                  if (n.ultima_etiqueta) {
                    const d = new Date(n.ultima_etiqueta);
                    if (!maxDataP || d > maxDataP) maxDataP = d;
                  }
                }
              });

              // Mesclar as notas em cada item de arrPlaca
              arrPlaca.forEach(item => {
                if (item.cdremetente && notasPorRemetenteP[item.cdremetente]) {
                  item.notas = notasPorRemetenteP[item.cdremetente];
                } else {
                  item.notas = [];
                }
              });
            }
          } catch (error) {
            void error;
          }

          setOnboardData(prev => ({
            ...prev,
            totaisPorCliente: arrPlaca,
            coletas: arrPlaca, // Sincronizar coletas com totaisPorCliente para a aba produtividade
            totals: {
              ...(prev.totals || {}),
              notas: Number(sumNotasP || 0),
              vols: Number(sumVolsP || 0),
              peso: Number(Number(sumPesoP || 0).toFixed(2)),
              m3: Number(Number(sumM3P || 0).toFixed(2)),
              falta: Math.round(Number(sumVolsNaoImpP || 0)),
              notasImpressas: sumNotasImpP,
              notasNaoImpressas: sumNotasNaoImpP,
              etiquetasImpressas: sumEtiquetasImpP,
              etiquetasNaoImpressas: sumEtiquetasNaoImpP,
              primeiraEtiqueta: minDataP,
              ultimaEtiqueta: maxDataP
            },
          }));
        }
      } catch (error) {
        void error;
      }

    } catch (error) {
      void error;
    }
  }, [onboardData?.vehicleData?.capacidadeIdeal]);




  // Verificar se o usuário tem veículo vinculado (uso em aberto ou do dia atual)
  useEffect(() => {
    const toLocalYYYYMMDD = (date) => {
      try {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      } catch (error) {
        void error;
        return '';
      }
    };

    const formatDateToYYYYMMDD = (dateString) => {
      if (!dateString) return '';
      try {
        return new Date(dateString).toISOString().split('T')[0];
      } catch (e) {
        console.error("Erro ao formatar data:", e);
        return '';
      }
    };

    const run = async () => {
      if (!user?.codigo) return;
      try {
        // Se AuthContext já trouxe o uso do dia, usar diretamente
        if (vehicleUsageToday) {
          const placaAuth = vehicleUsageToday?.veiculo?.placa || vehicleUsageToday?.pliculo || '';
          setVeiculoDoDia(vehicleUsageToday);
          setOnboardData(prev => ({
            ...prev,
            vehicleData: { ...(prev.vehicleData || {}), placa: placaAuth },
            dtUso: formatDateToYYYYMMDD(vehicleUsageToday.dtInicio || vehicleUsageToday.dtUso || '')
          }));
          setModalPlacaVisible(false);
          setLoading(false);

          return;
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diaLocal = toLocalYYYYMMDD(hoje);

        // Primeiro tenta localizar um uso em aberto para o usuário
        const usosUsuario = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo) });
        let usoAtual = null;
        if (Array.isArray(usosUsuario) && usosUsuario.length > 0) {
          usoAtual = usosUsuario.find((u) => !u.dtFim) || usosUsuario.find((u) => {
            try {
              const dtStr = String(u.dtUso || u.dtInicio || '').substring(0, 10);
              return dtStr === diaLocal;
            } catch {
              return false;
            }
          }) || null;
        }

        // Se não encontrou via lista geral, tenta busca direta por dia
        if (!usoAtual) {
          const usosDoDia = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo), dia: diaLocal });
          if (Array.isArray(usosDoDia) && usosDoDia.length > 0) {
            usoAtual = usosDoDia[0];
          }
        }

        if (usoAtual) {
          const placa = usoAtual?.veiculo?.placa || usoAtual?.placa || '';
          // Guardar o próprio registro de uso para garantir que o ID seja o do uso
          setVeiculoDoDia(usoAtual);
          setOnboardData(prev => ({
            ...prev,
            vehicleData: { ...(prev.vehicleData || {}), placa },
            dtUso: formatDateToYYYYMMDD(usoAtual.dtInicio || usoAtual.dtUso || '')
          }));
          setModalPlacaVisible(false);
          setLoading(false);
        } else {

          setVeiculoDoDia(null);
          setModalPlacaVisible(true);
          setLoading(false);
        }
      } catch (error) {
        void error;
        setModalPlacaVisible(true);
        setLoading(false);
      }
    };
    run();
  }, [user?.codigo, vehicleUsageToday]);

  // Callbacks globais para WS-Print para manter printerConnected reativo em todo o contexto
  useEffect(() => {
    wsPrintClient.setCallbacks({
      onOpen: ({ url }) => {
        void url;
        try { setPrinterConnected(false); } catch (error) { void error; }
        try { if (!printerEmulator) setPrinterEmulator('WebSocket'); } catch (error) { void error; }
      },
      onClose: () => {
        try {
          setPrinterConnected(false);
          setPrinterName(null);
          setPrinterDevice(null);
          setPrinterEmulator(null);
        } catch (error) { void error; }
      },
      onPrinter: (nameOrId) => {
        try {
          const display = typeof nameOrId === 'string' ? nameOrId : String(nameOrId || '');
          setPrinterName(display);
          if (display && display.trim() !== '') setPrinterConnected(true);
        } catch (error) { void error; }
      },
      onStatus: (st) => {
        try {
          if (Object.prototype.hasOwnProperty.call(st || {}, 'pairedWith')) {
            if (st.pairedWith) {
              setPrinterDevice(String(st.pairedWith));
              if (!printerEmulator) setPrinterEmulator('WebSocket');
            }
          }
          if (Object.prototype.hasOwnProperty.call(st || {}, 'uuid')) {
            if (st.uuid) setPrinterDevice(String(st.uuid));
          }
          // Ignorar flag 'connected' sem nome
        } catch (error) { void error; }
      }
    });
    // Não há cleanup necessário além de fechar o socket em fluxos específicos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      window.setarImpressora = (deviceName) => {
        const raw = typeof deviceName === 'string' ? deviceName.trim() : '';
        let normalizedName = raw;
        if (raw && ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']')))) {
          try {
            const parsed = JSON.parse(raw);
            normalizedName = String(
              parsed?.deviceName ??
              parsed?.printerName ??
              parsed?.nome ??
              parsed?.name ??
              parsed?.device?.name ??
              parsed?.device?.nome ??
              raw
            ).trim();
          } catch (error) {
            void error;
          }
        }
        normalizedName = normalizedName.replace(/^bluetooth[\s:|-]*/i, '').trim();
        if (!normalizedName || ['bluetooth', 'websocket', 'socket'].includes(normalizedName.toLowerCase())) return;

        try { setPrinterConnected(true); } catch (error) { void error; }
        try { setPrinterName(normalizedName); } catch (error) { void error; }
        try { setPrinterEmulator('Bluetooth'); } catch (error) { void error; }
        try { setPrinterDevice(normalizedName); } catch (error) { void error; }
        try {
          printerService.syncState({ connected: true, name: normalizedName, type: 'bluetooth', device: { nome: normalizedName } });
          if (printerService && printerService.callbacks && typeof printerService.callbacks.onConnect === 'function') {
            printerService.callbacks.onConnect({ type: 'bluetooth', device: normalizedName, status: 'connected' });
          }
        } catch (error) { void error; }
      };
    } catch (error) { void error; }
    return () => { try { delete window.setarImpressora; } catch (error) { void error; } };
  }, []);

  useEffect(() => {
    try {
      printerService.syncState({
        connected: printerConnected,
        name: printerName,
        type: printerType,
        device: printerDevice ? { uuid: printerDevice, nome: printerName } : null,
      });
    } catch (error) { void error; }
  }, [printerConnected, printerName, printerType, printerDevice]);

  useEffect(() => {
    try {
      if (printerType) {
        localStorage.setItem('printerType', printerType);
      } else {
        localStorage.removeItem('printerType');
      }
    } catch (error) {
      void error;
    }
  }, [printerType]);

  // Función para actualizar datos específicos
  const updateOnboardData = (newData) => {
    setOnboardData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  // Seleciona cliente/coleta atual para foco na aba de notas
  const selecionarCliente = (coleta) => {
    if (!coleta) {
      setClienteSelecionado(null);
      setCnpjAtual(null);
      return;
    }
    setClienteSelecionado(coleta);
    setCnpjAtual(coleta.CdInscricao || null);
  };

  // Função para atualizar totais
  const updateTotals = (newTotals) => {
    setOnboardData(prevData => ({
      ...prevData,
      totals: {
        ...prevData.totals,
        ...newTotals
      }
    }));
  };

  // Função para adicionar/atualizar empresa
  const updateEmpresaData = (empresaKey, newData) => {
    setOnboardData(prevData => ({
      ...prevData,
      empresasData: prevData.empresasData.map(empresa =>
        empresa.key === empresaKey
          ? { ...empresa, ...newData }
          : empresa
      )
    }));
  };

  const contextValue = {
    onboardData,
    setOnboardData,
    updateOnboardData,
    updateTotals,
    updateEmpresaData,
    buscarNotasTotais,
    loading,
    error,
    // Dados específicos para fácil acesso
    userInfo: onboardData.userInfo,
    vehicleData: onboardData.vehicleData,
    empresasData: onboardData.empresasData,
    totals: onboardData.totals,
    coletas: onboardData.coletas,
    cnpjAtual,
    clienteSelecionado,
    selecionarCliente,
    // Nota selecionada externamente
    notaAtual,
    setNotaAtual,
    // Printer data
    printerName,
    setPrinterName,
    printerType,
    setPrinterType,
    printerDevice,
    setPrinterDevice,
    printerConnected,
    setPrinterConnected,
    printerEmulator,
    setPrinterEmulator
    ,
    // Veículo do dia e modal de placa
    veiculoDoDia,
    setVeiculoDoDia,
    modalPlacaVisible,
    setModalPlacaVisible
  };

  return (
    <OnboardContext.Provider value={contextValue}>
      {children}
    </OnboardContext.Provider>
  );
};

// Hook personalizado para usar o contexto Onboard
// eslint-disable-next-line react-refresh/only-export-components
export const useOnboard = () => {
  const context = useContext(OnboardContext);
  if (context) return context;
  return {
    onboardData: {
      dtUso: '',
      userInfo: null,
      vehicleData: null,
      empresasData: [],
      totaisPorCliente: [],
      notasDoCliente: [],
      totals: {
        notas: 0,
        vols: 0,
        peso: 0,
        m3: 0,
        falta: 0,
        notasImpressas: 0,
        notasNaoImpressas: 0,
        etiquetasImpressas: 0,
        etiquetasNaoImpressas: 0,
        primeiraEtiqueta: null,
        ultimaEtiqueta: null
      },
      coletas: [],
    },
    setOnboardData: () => { },
    updateOnboardData: () => { },
    updateTotals: () => { },
    updateEmpresaData: () => { },
    buscarNotasTotais: async () => { },
    loading: true,
    error: null,
    userInfo: null,
    vehicleData: null,
    empresasData: [],
    totals: {
      notas: 0,
      vols: 0,
      peso: 0,
      m3: 0,
      falta: 0,
      notasImpressas: 0,
      notasNaoImpressas: 0,
      etiquetasImpressas: 0,
      etiquetasNaoImpressas: 0,
      primeiraEtiqueta: null,
      ultimaEtiqueta: null
    },
    coletas: [],
    cnpjAtual: null,
    clienteSelecionado: null,
    selecionarCliente: () => { },
    notaAtual: null,
    setNotaAtual: () => { },
    printerName: null,
    setPrinterName: () => { },
    printerType: getInitialPrinterType(),
    setPrinterType: () => { },
    printerDevice: null,
    setPrinterDevice: () => { },
    printerConnected: false,
    setPrinterConnected: () => { },
    printerEmulator: null,
    setPrinterEmulator: () => { },
    veiculoDoDia: null,
    setVeiculoDoDia: () => { },
    modalPlacaVisible: false,
    setModalPlacaVisible: () => { },
  };
};

export default OnboardContext;
